import { Fragment, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { COMPANY, LEGAL_LINKS } from "./Legaldata";

/* ───────────── text helpers ───────────── */

// Replace {{name}}, {{email}} … with COMPANY values
const fill = (text = "") =>
    text.replace(/\{\{(\w+)\}\}/g, (_, key) => COMPANY[key] ?? "");

const slugify = (s) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

// Split the single string into { intro, sections: [{ id, title, body }] } using "## " headings
function parseDocument(raw) {
    const text = fill(raw).replace(/\r\n/g, "\n").trim();
    const parts = text.split(/^## /m);
    const intro = parts[0].trim();
    const sections = parts.slice(1).map((part) => {
        const nl = part.indexOf("\n");
        const title = (nl === -1 ? part : part.slice(0, nl)).trim();
        const body = nl === -1 ? "" : part.slice(nl + 1).trim();
        return { id: slugify(title), title, body };
    });
    return { intro, sections };
}

// Inline: **bold** and [label](url)
const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

function Inline({ text }) {
    return text.split(INLINE).map((part, i) => {
        if (!part) return null;

        if (part.startsWith("**") && part.endsWith("**")) {
            return (
                <strong key={i} className="font-semibold text-slate-800">
                    {part.slice(2, -2)}
                </strong>
            );
        }

        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link) {
            const [, label, url] = link;
            const cls = "text-sky-600 hover:underline break-words";
            return url.startsWith("/") ? (
                <Link key={i} to={url} className={cls}>
                    {label}
                </Link>
            ) : (
                <a key={i} href={url} className={cls}>
                    {label}
                </a>
            );
        }

        return <Fragment key={i}>{part}</Fragment>;
    });
}

// Block level: paragraphs, "### " sub-headings, "- " bullet lists
function RichText({ content }) {
    const blocks = content
        .split(/\n\s*\n/)
        .map((b) => b.trim())
        .filter(Boolean);

    return blocks.map((block, i) => {
        if (block.startsWith("### ")) {
            // A sub-heading may be followed directly (no blank line) by text
            const [first, ...rest] = block.split("\n");
            return (
                <Fragment key={i}>
                    <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-2">{first.slice(4)}</h3>
                    {rest.length > 0 && <RichText content={rest.join("\n")} />}
                </Fragment>
            );
        }

        const lines = block.split("\n");

        if (lines.every((l) => l.trim().startsWith("- "))) {
            return (
                <ul
                    key={i}
                    className="list-disc pl-6 mb-4 space-y-2 text-slate-600 leading-relaxed marker:text-sky-500"
                >
                    {lines.map((l, j) => (
                        <li key={j}>
                            <Inline text={l.trim().slice(2)} />
                        </li>
                    ))}
                </ul>
            );
        }

        return (
            <p key={i} className="text-slate-600 leading-relaxed mb-4">
                {lines.map((l, j) => (
                    <Fragment key={j}>
                        {j > 0 && <br />}
                        <Inline text={l} />
                    </Fragment>
                ))}
            </p>
        );
    });
}

/* ───────────── page layout ───────────── */

/**
 * doc: { title: string, content: string }   (see legalData.js)
 */
export default function LegalLayout({ doc }) {
    const { title, content } = doc;
    const { intro, sections } = useMemo(() => parseDocument(content), [content]);

    useEffect(() => {
        window.scrollTo({ top: 0 });
        document.title = `${title} | ${COMPANY.name}`;
    }, [title]);

    const jump = (e, id) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <main className="bg-blue-50 min-h-screen w-full">
            {/* Header */}
            <header className="bg-gradient-to-r from-blue-900 to-sky-500 text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
                    <nav className="text-xs sm:text-sm text-sky-100 mb-3" aria-label="Breadcrumb">
                        <Link to="/" className="hover:underline">
                            Home
                        </Link>
                        <span className="mx-2">/</span>
                        <span>{title}</span>
                    </nav>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">{title}</h1>
                    <p className="mt-3 text-sm sm:text-base text-sky-100">
                        Last updated: {COMPANY.lastUpdated}
                    </p>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 lg:gap-12">
                {/* Table of contents (built from the "## " headings) */}
                <aside className="lg:sticky lg:top-24 self-start">
                    <nav
                        aria-label="Table of contents"
                        className="rounded-2xl bg-white shadow-sm border border-sky-100 p-5"
                    >
                        <p className="text-sm font-semibold text-slate-800 mb-3">On this page</p>
                        <ol className="space-y-2 text-sm">
                            {sections.map((s, i) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        onClick={(e) => jump(e, s.id)}
                                        className="text-slate-500 hover:text-blue-900 transition-colors"
                                    >
                                        {i + 1}. {s.title}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>
                </aside>

                {/* Content */}
                <article className="rounded-2xl bg-white shadow-sm border border-sky-100 p-6 sm:p-10">
                    {intro && (
                        <div className="mb-8 pb-8 border-b border-slate-100">
                            <RichText content={intro} />
                        </div>
                    )}

                    {sections.map((s, i) => (
                        <section key={s.id} id={s.id} className="scroll-mt-24 mb-10 last:mb-0">
                            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                                {i + 1}. {s.title}
                            </h2>
                            <RichText content={s.body} />
                        </section>
                    ))}

                    {/* Cross links */}
                    <div className="mt-12 pt-6 border-t border-slate-100 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        {LEGAL_LINKS.map((l) => (
                            <Link key={l.to} to={l.to} className="text-sky-600 hover:underline">
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </article>
            </div>
        </main>
    );
}